import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { ArrowLeft, BarChart3, Calendar, TrendingUp, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const QuarterStatus = () => {
  const [selectedQuarter, setSelectedQuarter] = useState('');

  // Handler functions for CSP compliance
  const handleQuarterChange = (e) => {
    setSelectedQuarter(e.target.value);
  };

  // Fetch quarters for dropdown
  const { data: quarters } = useQuery(
    ['quarters'],
    async () => {
      const response = await axios.get('/api/masters/quarters');
      return response.data;
    }
  );

  // Fetch quarter status data
  const { data: quarterStatus, isLoading } = useQuery(
    ['quarter-status', selectedQuarter],
    async () => {
      if (!selectedQuarter) return null;
      const response = await axios.get(`/api/status/quarter/${selectedQuarter}`);
      return response.data;
    },
    {
      enabled: !!selectedQuarter
    }
  );

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return '#10B981';
      case 'in progress': return '#3B82F6';
      case 'on hold': return '#F59E0B';
      case 'not started': return '#6B7280';
      default: return '#6B7280';
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'Completed';
      case 'in progress': return 'In Progress';
      case 'on hold': return 'On Hold';
      case 'not started': return 'Not Started';
      default: return 'Unknown';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Quarter Wise Status</h1>
      </div>

      {/* Quarter Selection */}
      <div className="card">
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <Target className="h-5 w-5 text-gray-400" />
            <label className="text-sm font-medium text-gray-700">Quarter:</label>
            <select
              value={selectedQuarter}
              onChange={handleQuarterChange}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Select Quarter...</option>
              {quarters?.map((quarter) => (
                <option key={quarter.id} value={quarter.id}>
                  {quarter.quarter_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {selectedQuarter && (
        <>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <>
              {/* Quarter Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="card">
                  <div className="flex items-center">
                    <BarChart3 className="h-8 w-8 text-blue-600" />
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">Total Tasks</h3>
                      <p className="text-2xl font-bold text-blue-600">{quarterStatus?.summary?.totalTasks || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center">
                    <TrendingUp className="h-8 w-8 text-green-600" />
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">Completed</h3>
                      <p className="text-2xl font-bold text-green-600">{quarterStatus?.summary?.completedTasks || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center">
                    <Target className="h-8 w-8 text-yellow-600" />
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">In Progress</h3>
                      <p className="text-2xl font-bold text-yellow-600">{quarterStatus?.summary?.inProgressTasks || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center">
                    <Calendar className="h-8 w-8 text-purple-600" />
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">Completion Rate</h3>
                      <p className="text-2xl font-bold text-purple-600">{quarterStatus?.summary?.completionRate || 0}%</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Task Status by Cell - Bar Chart */}
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2" />
                  Task Status by Cell
                </h3>
                <div className="space-y-6">
                  {quarterStatus?.cellTaskStatus?.map((cellData) => (
                    <div key={cellData.cell_id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-gray-900">Cell {cellData.cell_no}</h4>
                        <span className="text-sm text-gray-600">{cellData.state_name}</span>
                      </div>
                      
                      {/* Status Bars */}
                      <div className="space-y-3">
                        {cellData.taskStatus?.map((task) => (
                          <div key={task.task_id} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700">Task {task.task_no}: {task.task_name}</span>
                              <span className="text-sm text-gray-600">{task.total_subtasks} subtasks</span>
                            </div>
                            <div className="flex space-x-1 h-6 rounded-lg overflow-hidden">
                              {task.completed_count > 0 && (
                                <div
                                  className="flex items-center justify-center text-xs font-medium text-white bg-green-600"
                                  style={{ width: `${(task.completed_count / task.total_subtasks) * 100}%` }}
                                  title={`Completed: ${task.completed_count}`}
                                >
                                  {task.completed_count}
                                </div>
                              )}
                              {task.in_progress_count > 0 && (
                                <div
                                  className="flex items-center justify-center text-xs font-medium text-white bg-blue-600"
                                  style={{ width: `${(task.in_progress_count / task.total_subtasks) * 100}%` }}
                                  title={`In Progress: ${task.in_progress_count}`}
                                >
                                  {task.in_progress_count}
                                </div>
                              )}
                              {task.on_hold_count > 0 && (
                                <div
                                  className="flex items-center justify-center text-xs font-medium text-white bg-yellow-600"
                                  style={{ width: `${(task.on_hold_count / task.total_subtasks) * 100}%` }}
                                  title={`On Hold: ${task.on_hold_count}`}
                                >
                                  {task.on_hold_count}
                                </div>
                              )}
                              {task.not_started_count > 0 && (
                                <div
                                  className="flex items-center justify-center text-xs font-medium text-white bg-gray-600"
                                  style={{ width: `${(task.not_started_count / task.total_subtasks) * 100}%` }}
                                  title={`Not Started: ${task.not_started_count}`}
                                >
                                  {task.not_started_count}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center space-x-4 text-xs text-gray-500">
                              {task.completed_count > 0 && (
                                <div className="flex items-center">
                                  <div className="w-3 h-3 rounded-full mr-1 bg-green-600"></div>
                                  Completed: {task.completed_count}
                                </div>
                              )}
                              {task.in_progress_count > 0 && (
                                <div className="flex items-center">
                                  <div className="w-3 h-3 rounded-full mr-1 bg-blue-600"></div>
                                  In Progress: {task.in_progress_count}
                                </div>
                              )}
                              {task.on_hold_count > 0 && (
                                <div className="flex items-center">
                                  <div className="w-3 h-3 rounded-full mr-1 bg-yellow-600"></div>
                                  On Hold: {task.on_hold_count}
                                </div>
                              )}
                              {task.not_started_count > 0 && (
                                <div className="flex items-center">
                                  <div className="w-3 h-3 rounded-full mr-1 bg-gray-600"></div>
                                  Not Started: {task.not_started_count}
                                </div>
                              )}
                            </div>
                            
                            {/* Training Data for Task 3 */}
                            {task.task_id === 3 && task.training_data && (
                              <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                                <h5 className="text-sm font-medium text-blue-900 mb-2">Training Details:</h5>
                                <div className="space-y-2">
                                  {task.training_data.map((training, idx) => (
                                    <div key={idx} className="text-xs text-blue-800">
                                      <strong>{training.training_name}</strong> - {training.mode} - {training.attendees} attendees - {new Date(training.date).toLocaleDateString()}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Building Data for Task 2 and 4 */}
                            {(task.task_id === 2 || task.task_id === 4) && task.building_data && (
                              <div className="mt-3 p-3 bg-green-50 rounded-lg">
                                <h5 className="text-sm font-medium text-green-900 mb-2">Building Details:</h5>
                                <div className="space-y-2">
                                  {task.building_data.map((building, idx) => (
                                    <div key={idx} className="text-xs text-green-800">
                                      <strong>{building.building_name}</strong> - {building.address} - {building.project_type} - Status: {building.status}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Overall Progress Chart */}
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2" />
                  Overall Progress by Task
                </h3>
                <div className="space-y-4">
                  {quarterStatus?.overallProgress?.map((task) => (
                    <div key={task.task_id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">{task.task_name}</h4>
                        <span className="text-sm font-medium text-gray-600">
                          {task.completion_rate}% Complete
                        </span>
                      </div>
                      <div className="space-y-2">
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className="bg-primary-600 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${task.completion_rate}%` }}
                          ></div>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>{task.completed_subtasks} completed</span>
                          <span>{task.total_subtasks} total</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Monthly Breakdown */}
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  Monthly Breakdown
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left border-b">
                        <th className="pb-2">Month</th>
                        <th className="pb-2">Tasks Started</th>
                        <th className="pb-2">Tasks Completed</th>
                        <th className="pb-2">Progress Rate</th>
                        <th className="pb-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quarterStatus?.monthlyBreakdown?.map((month) => (
                        <tr key={month.month} className="border-b">
                          <td className="py-2 font-medium">{month.month_name}</td>
                          <td className="py-2">{month.tasks_started}</td>
                          <td className="py-2">{month.tasks_completed}</td>
                          <td className="py-2">
                            <div className="flex items-center space-x-2">
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-primary-600 h-2 rounded-full"
                                  style={{ width: `${month.progress_rate}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-600">{month.progress_rate}%</span>
                            </div>
                          </td>
                          <td className="py-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              month.progress_rate >= 80 ? 'text-green-600 bg-green-100' :
                              month.progress_rate >= 60 ? 'text-blue-600 bg-blue-100' :
                              month.progress_rate >= 40 ? 'text-yellow-600 bg-yellow-100' :
                              'text-red-600 bg-red-100'
                            }`}>
                              {month.progress_rate >= 80 ? 'Excellent' :
                               month.progress_rate >= 60 ? 'Good' :
                               month.progress_rate >= 40 ? 'Fair' : 'Needs Improvement'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default QuarterStatus;
